import {Signal, TemplateRef, WritableSignal, signal} from '@angular/core';
import {Observable, Subject} from 'rxjs';

import {TLabel} from '@mydeals/i18n';

export class ModalRef<TResult = unknown, TData = unknown> {
  private readonly _title: WritableSignal<TLabel | undefined>;
  private readonly _footer: WritableSignal<TemplateRef<unknown> | null> = signal(null);
  private readonly _visible: WritableSignal<boolean> = signal(false);
  private readonly _closed$ = new Subject<TResult | undefined>();
  private isClosed = false;

  readonly closed$: Observable<TResult | undefined> = this._closed$.asObservable();
  readonly title: Signal<TLabel | undefined>;
  readonly footer: Signal<TemplateRef<unknown> | null> = this._footer.asReadonly();
  /** Drives the modal enter animation (close tears the modal down immediately). */
  readonly visible: Signal<boolean> = this._visible.asReadonly();

  constructor(
    readonly data: TData | undefined,
    initialTitle: TLabel | undefined,
  ) {
    this._title = signal(initialTitle);
    this.title = this._title.asReadonly();
  }

  /** Whether close() has already been requested. */
  get closed(): boolean {
    return this.isClosed;
  }

  /** Host: start the enter animation. Never re-shows a modal that is already closing. */
  show(): void {
    if (this.isClosed) return;
    this._visible.set(true);
  }

  setFooter(template: TemplateRef<unknown> | null): void {
    this._footer.set(template);
  }

  close(result?: TResult): void {
    if (this.isClosed) return;
    this.isClosed = true;
    // Emitting drives ModalService to remove the modal from the DOM immediately
    // (instant close — no p-dialog leave animation, so no end-of-leave flash).
    this._closed$.next(result);
    this._closed$.complete();
  }
}
